import React, { useState } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import MDButton from "components/MDButton";
import ControlledSelect from "components/FormInputs/ControlledSelect";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { useUploadFileMutation } from "services/files";
import { convertToOrderSchema } from "../schemas/_model";

import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";

registerPlugin(FilePondPluginImagePreview);

const paymentMethods = [
  { value: "BANK", label: "Bank" },
  { value: "CREDIT", label: "Credit" },
  { value: "CARD", label: "Card" },
];

const paymentStatuses = [
  { value: "PENDING", label: "Pending" },
  { value: "PAID", label: "Paid" },
  { value: "CANCELED", label: "Canceled" },
];

const ConvertToOrderDialog = ({ open, onClose, onSubmit, initialValues = {}, loading }) => {
  const [file, setFile] = useState([]);
  const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();

  const { control, handleSubmit, reset, formState } = useForm({
    resolver: yupResolver(convertToOrderSchema),
    defaultValues: {
      paymentMethod: initialValues.paymentMethod || "",
      paymentStatus: initialValues.paymentStatus || "",      
    },
    mode: "onChange",
  });

  const handleUpdateFiles = (files) => {
    if (files.length > 0) {
      let photos = [];
      for (let file of files) {
        photos.push(file.source);
      }
      setFile(photos);
    } else {
      setFile([]);
    }
  };

  const handleDialogClose = () => {
    reset();
    setFile([]);
    onClose();
  };

  const internalSubmit = async (data) => {
    let paymentProof = "";
    if (file && file.length > 0) {
      let formData = new FormData();
      formData.append("file", file[0]);
      const uploadResponse = await uploadFile({ formData: formData, directory: "reciepts" });
      if (uploadResponse.data?.status === "success") {
        paymentProof = uploadResponse.data.response.path.replace(/\\/g, "/");
      } else {
        Notify.failure("Failed to upload bank receipt");
        return;
      }
    }
    console.log({ ...data, paymentProof });
    onSubmit({ ...data, paymentProof });
    // handleDialogClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, color: "#4b4b4f" }}>Convert Quote to Order</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleDialogClose}
        sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ControlledSelect
                name="paymentMethod"
                control={control}
                label="Payment Method *"
                menu={{
                  id: "value",
                  value: "value",
                  title: "Payment Methods",
                  list: paymentMethods,
                }}
                sx={{
                    "& .MuiFormLabel-root": {
                      backgroundColor: "#fff",
                    },
                    "& .MuiInputBase-root": {
                      height: "44px",
                    },
                  }}
                defaultValue=""
                error={!!formState.errors.paymentMethod}
                helperText={formState.errors.paymentMethod?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledSelect
                name="paymentStatus"
                control={control}
                label="Payment Status *"
                menu={{
                  id: "value",
                  value: "value",
                  title: "Payment Statuses",
                  list: paymentStatuses,
                }}
                sx={{
                    "& .MuiFormLabel-root": {
                      backgroundColor: "#fff",
                    },
                    "& .MuiInputBase-root": {
                      height: "44px",
                    },
                  }}
                defaultValue=""
                error={!!formState.errors.paymentStatus}
                helperText={formState.errors.paymentStatus?.message}
              />
            </Grid>           
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Bank Receipt
                </Typography>
                <Box
                  component={FilePond}
                  instantUpload={false}
                  allowFileSizeValidation={true}
                  name="file"
                  allowFileTypeValidation={true}
                  acceptedFileTypes={[
                    "image/jpeg",
                    "image/jpg",
                    "image/png",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "application/pdf",
                  ]}
                  allowMultiple={false}
                  allowFilePoster={false}
                  labelIdle={'Drag & Drop bank receipt or <span class="filepond--label-action">Browse</span>'}
                  onupdatefiles={handleUpdateFiles}
                  allowRevert={false}
                />
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleDialogClose} color="secondary" variant="gradient">
          Cancel
        </MDButton>
        <MDButton
          type="submit"
          color="primary"
          variant="gradient"
          onClick={handleSubmit(internalSubmit)}
          disabled={loading || isFileUploading}
        >
          Convert
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

ConvertToOrderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  loading: PropTypes.bool,
};

export default ConvertToOrderDialog; 